import React from 'react'
import { Loader2 } from 'lucide-react'

// Re-export the standard admin-panel primitives so tabs can pull everything from one place.
export { Button } from '../../components/ui/button'
export { Badge } from '../../components/ui/badge'
export { Input } from '../../components/ui/input'
export { Select } from '../../components/ui/select'
export {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
    CardFooter,
} from '../../components/ui/card'
export {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '../../components/ui/table'

/**
 * SectionTitle — page header used inside each tab.
 * Title + description, optional right-side actions slot.
 */
export function SectionTitle({
    title,
    description,
    actions,
}: {
    title: string
    description?: string
    actions?: React.ReactNode
}) {
    return (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
                <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                {description && (
                    <p className="mt-1 text-sm text-gray-500">{description}</p>
                )}
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
    )
}

/**
 * Eyebrow — small uppercase label above a section.
 */
export function Eyebrow({ children }: { children: React.ReactNode }) {
    return (
        <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
            {children}
        </p>
    )
}

/**
 * Field — labeled text input wrapper for forms.
 */
export function Field({
    label,
    hint,
    children,
    required,
}: {
    label: string
    hint?: string
    children: React.ReactNode
    required?: boolean
}) {
    return (
        <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
                {label}
                {required && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            {children}
            {hint && <p className="text-xs text-gray-500">{hint}</p>}
        </div>
    )
}

/**
 * TextField — back-compat wrapper so existing tabs keep working.
 * For multiline content use the new RichTextEditor instead.
 */
export function TextField({
    label,
    value,
    onChange,
    placeholder,
    multiline,
    rows = 4,
    type = 'text',
    hint,
}: {
    label: string
    value: string
    onChange: (value: string) => void
    placeholder?: string
    multiline?: boolean
    rows?: number
    type?: string
    hint?: string
}) {
    const inputClass =
        'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
    return (
        <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">{label}</label>
            {multiline ? (
                <textarea
                    value={value}
                    rows={rows}
                    placeholder={placeholder}
                    onChange={e => onChange(e.target.value)}
                    className={`${inputClass} font-mono text-xs`}
                />
            ) : (
                <input
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    onChange={e => onChange(e.target.value)}
                    className={inputClass}
                />
            )}
            {hint && <p className="text-xs text-gray-500">{hint}</p>}
        </div>
    )
}

/**
 * Toggle — labeled on/off switch.
 */
export function Toggle({
    label,
    description,
    checked,
    onChange,
    disabled,
}: {
    label?: string
    description?: string
    checked: boolean
    onChange: (next: boolean) => void
    disabled?: boolean
}) {
    return (
        <label
            className={`flex items-start gap-3 ${
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
            }`}
        >
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => onChange(!checked)}
                className={`relative mt-0.5 inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                    checked ? 'bg-blue-600' : 'bg-gray-300'
                }`}
            >
                <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                        checked ? 'translate-x-[18px]' : 'translate-x-1'
                    }`}
                />
            </button>
            {(label || description) && (
                <div className="text-sm">
                    {label && <div className="font-medium text-gray-900">{label}</div>}
                    {description && (
                        <div className="mt-0.5 text-xs text-gray-500">{description}</div>
                    )}
                </div>
            )}
        </label>
    )
}

/**
 * PillButton — back-compat wrapper. Now renders a standard admin-panel button.
 */
export function PillButton({
    children,
    onClick,
    disabled,
    variant = 'default',
    type = 'button',
}: {
    children: React.ReactNode
    onClick?: () => void
    disabled?: boolean
    variant?: 'default' | 'primary' | 'danger' | 'ghost'
    type?: 'button' | 'submit' | 'reset'
}) {
    const base =
        'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50'
    const styles =
        variant === 'primary'
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : variant === 'danger'
              ? 'bg-red-600 text-white hover:bg-red-700'
              : variant === 'ghost'
                ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
    return (
        <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${styles}`}>
            {children}
        </button>
    )
}

/**
 * EmptyState — neutral placeholder for empty lists/tables.
 */
export function EmptyState({
    icon: Icon,
    title,
    description,
    action,
}: {
    icon?: React.ComponentType<{ className?: string }>
    title: string
    description?: string
    action?: React.ReactNode
}) {
    return (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
            {Icon && (
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                    <Icon className="h-6 w-6 text-gray-400" />
                </div>
            )}
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            {description && (
                <p className="mt-1 max-w-md text-sm text-gray-500">{description}</p>
            )}
            {action && <div className="mt-4">{action}</div>}
        </div>
    )
}

/**
 * Spinner — quick inline spinner for loading states.
 */
export function Spinner({ className = '' }: { className?: string }) {
    return <Loader2 className={`h-4 w-4 animate-spin text-gray-400 ${className}`} />
}

/**
 * LoadingPanel — centered spinner with optional caption.
 */
export function LoadingPanel({ label = 'Loading…' }: { label?: string }) {
    return (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white py-12 text-sm text-gray-500">
            <Spinner /> {label}
        </div>
    )
}

/**
 * Modal — simple centered modal used for create/edit dialogs.
 */
export function Modal({
    open,
    onClose,
    title,
    description,
    children,
    footer,
    width = 'md',
}: {
    open: boolean
    onClose: () => void
    title: string
    description?: string
    children: React.ReactNode
    footer?: React.ReactNode
    width?: 'sm' | 'md' | 'lg' | 'xl'
}) {
    if (!open) return null
    const widthCls = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
    }[width]
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={onClose}
        >
            <div
                className={`flex max-h-[90vh] w-full ${widthCls} flex-col overflow-hidden rounded-lg bg-white shadow-2xl`}
                onClick={e => e.stopPropagation()}
            >
                <div className="border-b border-gray-200 px-6 py-4">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    {description && (
                        <p className="mt-1 text-sm text-gray-500">{description}</p>
                    )}
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
                {footer && (
                    <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-3">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    )
}

/**
 * ConfirmDialog — replaces native confirm() prompts.
 */
export function ConfirmDialog({
    open,
    onCancel,
    onConfirm,
    title,
    message,
    confirmLabel = 'Confirm',
    confirmVariant = 'primary',
    loading,
}: {
    open: boolean
    onCancel: () => void
    onConfirm: () => void
    title: string
    message: string
    confirmLabel?: string
    confirmVariant?: 'primary' | 'danger'
    loading?: boolean
}) {
    return (
        <Modal
            open={open}
            onClose={onCancel}
            title={title}
            footer={
                <>
                    <PillButton onClick={onCancel} disabled={loading}>
                        Cancel
                    </PillButton>
                    <PillButton
                        onClick={onConfirm}
                        disabled={loading}
                        variant={confirmVariant === 'danger' ? 'danger' : 'primary'}
                    >
                        {loading && <Spinner className="!h-3.5 !w-3.5 text-white" />}
                        {confirmLabel}
                    </PillButton>
                </>
            }
        >
            <p className="text-sm text-gray-700">{message}</p>
        </Modal>
    )
}

/**
 * StatCard — small numeric tile used for delivery dashboards.
 */
export function StatCard({
    label,
    value,
    sub,
}: {
    label: string
    value: React.ReactNode
    sub?: React.ReactNode
}) {
    return (
        <div className="rounded-lg border border-gray-200 bg-white px-5 py-4">
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                {label}
            </div>
            <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
            {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
        </div>
    )
}
